import { asEuro, type AmountUnit } from '@/utils/amount-unit'
import { toNumber } from '@/utils/number'

type EstateObjectData = App.Data.EstateObject.EstateObjectData
type ModernizationData = App.Data.EstateObject.ModernizationData

export const additionalPurchaseCostKeys = [
  'land_register_charge',
  'broker_commission',
  'notary_land_registry_fees',
] as const

export type AdditionalPurchaseCostKey = (typeof additionalPurchaseCostKeys)[number]

/** Kaufnebenkosten in Euro — each position is stored either as a percentage of the purchase price or as a fixed amount. */
export const additionalPurchaseCosts = (
  purchasePrice: number | null,
  amounts: Record<AdditionalPurchaseCostKey, number | null>,
  units: Record<AdditionalPurchaseCostKey, AmountUnit | null>,
): number =>
  additionalPurchaseCostKeys.reduce(
    (total, key) => total + (asEuro(amounts[key], units[key] ?? 'percent', purchasePrice) ?? 0),
    0,
  )

/** Gesamtinvestition of a single property: purchase price plus its Kaufnebenkosten. */
export const estateObjectInvestment = (estateObject: EstateObjectData): number | null => {
  const purchasePrice = toNumber(estateObject.purchase_price)

  if (purchasePrice === null) {
    return null
  }

  return (
    purchasePrice +
    additionalPurchaseCosts(
      purchasePrice,
      {
        land_register_charge: estateObject.land_register_charge ?? null,
        broker_commission: estateObject.broker_commission ?? null,
        notary_land_registry_fees: estateObject.notary_land_registry_fees ?? null,
      },
      {
        land_register_charge: estateObject.land_register_charge_unit ?? null,
        broker_commission: estateObject.broker_commission_unit ?? null,
        notary_land_registry_fees: estateObject.notary_land_registry_fees_unit ?? null,
      },
    )
  )
}

/** Modernisierungskosten of a property: the sum of its recorded modernizations. */
export const modernizationCosts = (modernizations: ModernizationData[]): number =>
  modernizations.reduce((total, modernization) => total + (toNumber(modernization.price) ?? 0), 0)
